import type { OnboardingContext } from "@/lib/tracking/onboarding";
import { getStoredAdEventType } from "@/lib/quizEventType";
import { hasURLAudience } from "@/lib/quizAudience";
import { ageFromBirthday, ageToBucket } from "./age";
import type { OnboardingAnswers } from "./types";

/**
 * The segments every onboarding event is stamped with, built in one place.
 *
 * The runner fires the step events and the host page fires the completion, so
 * without this they would each assemble their own idea of "who this was" — and
 * a funnel whose first and last steps disagree about the language or the arm
 * cannot be read at all.
 *
 * Everything here is knowable at any point in the flow. The two that are not —
 * the age bracket and the city — are simply null until their question has been
 * asked, which is itself the honest answer for the steps before them.
 */
export function buildOnboardingContext(args: {
  variant: string;
  audience: string;
  language: string;
  answers: OnboardingAnswers;
  isSignedIn: boolean;
}): OnboardingContext {
  const { variant, audience, language, answers, isSignedIn } = args;
  return {
    variant,
    audience,
    // Stripped to the language, not the locale: "de-AT" and "de" are the same
    // copy, and splitting the funnel by region would only thin the counts.
    language: (language || "en").split("-")[0],
    adEventType: getStoredAdEventType(),
    communityPinned: hasURLAudience(),
    ageBucket: ageToBucket(ageFromBirthday(answers.birthday as string | undefined)),
    city: (answers.location as string | undefined) ?? null,
    isSignedIn,
  };
}
